import { MouseEvent } from 'react';
import styled from '@/styles';
import { dependabilityV2Colors } from '@/styles/colors';
import {
  isWorkerSuspended,
  SUSPENDED_BADGE_LABEL,
} from '@/util/dependabilityBadge';
import { tierColor } from './tierColors';
import { InfoTipIcon } from './icons';

// GRAVY-10347: the WHOLE pill opens the tier popover. QA flagged that only the
// ~30px info icon was clickable while the label was an inert <span>, which is a
// small touch target and misses the AC intent ("the tier badge has a clickable
// info affordance"). When `onInfoClick` is set the pill renders as a <button>
// spanning the full label — the button chrome is reset so it looks identical to
// the plain-span (non-interactive) form.
const Pill = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '4px 8px',
  borderRadius: '8px',
  color: '#FFFFFF',
  fontSize: '$sm',
  fontWeight: 700,
  variants: {
    interactive: {
      true: {
        appearance: 'none',
        border: 'none',
        fontFamily: 'inherit',
        fontSize: '$sm',
        fontWeight: 700,
        lineHeight: 'inherit',
        letterSpacing: 'inherit',
        cursor: 'pointer',
      },
    },
  },
});

// Trailing info glyph — now purely decorative (the pill itself is the button),
// so it is hidden from assistive tech.
const InfoIcon = styled('span', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#FFFFFF',
});

// Medal glyph leading the pill (Figma export 37920:11819) — white, so it
// reads on every tier background.
const MedalIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    data-testid="tier-pill-medal"
  >
    <path
      d="M15.1266 4.00024C15.1266 8.2402 13.6066 13.2005 8.16666 15.2004C2.66279 13.1525 1.49951 6.88034 1.60611 4.00024L8.16666 0.800049L15.1266 4.00024ZM7.02994 6.22388L4.06021 6.61548L6.23306 8.67798L5.68716 11.6243L8.32095 10.1956L10.9538 11.6243L10.4088 8.67798L12.5817 6.61548L9.61099 6.22388L8.32095 3.51978L7.02994 6.22388Z"
      fill="currentColor"
    />
  </svg>
);

interface Props {
  tier: string;
  label: string;
  onInfoClick?: () => void;
  // open/closed state of the tier popover this button controls — surfaced as
  // aria-expanded so assistive tech gets the state (fix round 3)
  expanded?: boolean;
  // GRAVY-10404: DISPLAY-ONLY. A suspended / permanently-removed worker shows a
  // dark "Suspended" pill in place of the tier — distinct from Monitored (red)
  // and the no-show caution badge. The score number is untouched.
  accountabilityStatus?: string | null;
  // GRAVY-10404: authoritative active-suspension flag from the backend (reflects
  // an active suspension record — temporary OR permanent — the same signal the
  // suspension banner uses). When true the pill shows "Suspended" regardless of
  // accountabilityStatus, so the badge agrees with the banner even if the
  // denormalized status column lags. Display-only.
  isSuspended?: boolean | null;
}

const TierPill = ({
  tier,
  label,
  onInfoClick,
  expanded = false,
  accountabilityStatus,
  isSuspended,
}: Props) => {
  const suspended = isWorkerSuspended(accountabilityStatus, isSuspended);
  const interactive = Boolean(onInfoClick);
  const pillLabel = suspended ? SUSPENDED_BADGE_LABEL : label;

  const content = (
    <>
      {/* The medal implies a tier ranking, which a suspended worker no longer
          holds — so it is dropped when suspended, leaving a plain dark chip. */}
      {!suspended && <MedalIcon />}
      {pillLabel}
      {interactive && (
        <InfoIcon aria-hidden="true">
          <InfoTipIcon />
        </InfoIcon>
      )}
    </>
  );

  const background = suspended
    ? dependabilityV2Colors.depSuspended
    : tierColor(tier);

  if (!interactive) {
    return (
      <Pill
        css={{ backgroundColor: background }}
        data-testid="tier-pill"
        data-suspended={suspended || undefined}
      >
        {content}
      </Pill>
    );
  }

  return (
    <Pill
      as="button"
      type="button"
      interactive
      css={{ backgroundColor: background }}
      data-testid="tier-pill"
      data-suspended={suspended || undefined}
      aria-label="Dependability tier info"
      aria-haspopup="dialog"
      aria-expanded={expanded}
      // GRAVY-10233: stop the opening click here. TierPopover's shared Popover
      // arms a window-level `useClickout` the moment it opens; without this, the
      // very click that opened the popover keeps bubbling to window, the clickout
      // listener fires, and the popover closes ~1ms after it opened — so a real
      // user click flashed it open then shut ("nothing happens"). (Reproduced on
      // live QA; jest/dispatchEvent miss the race itself as they don't bubble the
      // real click to window under the same effect timing.)
      onClick={(e: MouseEvent) => {
        e.stopPropagation();
        onInfoClick?.();
      }}
    >
      {content}
    </Pill>
  );
};

export default TierPill;
